'use client';
import { useUser } from '@clerk/nextjs';
import type { ReactNode } from 'react';
// this whole meta pixel topic had very bad documentation
// this is heavily AI coded
import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import {
  type ConsentStatus,
  getCookieConsent,
  handleGoogleAnalyticsConsent,
  handleMetaPixelConsent,
  setCookieConsent,
} from '@/app/utils/consentUtils';

interface CookieConsentContextType {
  consentGiven: ConsentStatus;
  isMounted: boolean;
  hasConsent: boolean;
  isUndecided: boolean;
  acceptCookies: () => Promise<void>;
  declineCookies: () => Promise<void>;
}

const CookieConsentContext = createContext<CookieConsentContextType | undefined>(undefined);

export const useCookieConsent = () => {
  const context = useContext(CookieConsentContext);
  if (context === undefined) {
    throw new Error('useCookieConsent must be used within a CookieConsentProvider');
  }
  return context;
};

interface CookieConsentProviderProps {
  children: ReactNode;
}

export const CookieConsentProvider: React.FC<CookieConsentProviderProps> = ({ children }) => {
  const [consentGiven, setConsentGiven] = useState<ConsentStatus>('undecided');
  const [isMounted, setIsMounted] = useState(false);
  const { user, isSignedIn } = useUser();
  const isSyncingRef = useRef(false);
  const lastSyncedConsentRef = useRef<ConsentStatus | null>(null);

  useEffect(() => {
    const initializeConsent = async () => {
      // First check local storage for immediate fallback
      const localConsent = getCookieConsent();

      // If user is signed in, check their account metadata
      if (isSignedIn && user) {
        const userConsent = user.publicMetadata?.cookieConsent as ConsentStatus;

        if (userConsent === 'accepted') {
          // User previously accepted - auto-accept for seamless experience
          setConsentGiven('accepted');
          setCookieConsent('accepted'); // Sync to local storage
          lastSyncedConsentRef.current = 'accepted';
        } else if (userConsent === 'rejected' && localConsent === 'undecided') {
          // User previously rejected but has no local preference - show banner to give them another chance
          setConsentGiven('undecided');
        } else if (userConsent === 'rejected' && localConsent === 'rejected') {
          // User previously rejected and local storage confirms - respect the choice
          setConsentGiven('rejected');
          lastSyncedConsentRef.current = 'rejected';
        } else if (userConsent === 'undecided' && localConsent === 'undecided') {
          // User has no preference anywhere - show banner
          setConsentGiven('undecided');
        } else if (localConsent !== 'undecided' && localConsent !== userConsent) {
          // User has local consent but not in account - sync to account (only if different)
          if (!isSyncingRef.current) {
            isSyncingRef.current = true;
            await updateUserConsent(localConsent);
            lastSyncedConsentRef.current = localConsent;
            isSyncingRef.current = false;
          }
          setConsentGiven(localConsent);
        } else {
          // No consent anywhere
          setConsentGiven('undecided');
        }
      } else {
        // Not signed in, use local storage only
        setConsentGiven(localConsent);
      }
    };

    // Initialize consent and set mounted only after all initialization is complete
    initializeConsent().then(() => {
      setIsMounted(true);
    });
  }, [isSignedIn, user]);

  const updateUser = async (consent: ConsentStatus) => {
    // Update data via an API endpoint
    const updateMetadata = await fetch('/api/updateMetadata', {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({
        cookieConsent: consent,
      }),
      cache: 'no-store',
    });

    const json = (await updateMetadata.json().catch(() => ({}))) as Record<string, unknown>;
    if (updateMetadata.status !== 200 || json?.message !== 'success') {
      throw new Error('Error updating');
    }
    // Do not call user.reload() here to avoid re-running effects and loops.
  };

  const updateUserConsent = async (consent: ConsentStatus) => {
    if (isSignedIn && user) {
      try {
        // Only sync if different from what we last synced to avoid loops
        if (lastSyncedConsentRef.current === consent) {
          return;
        }
        await updateUser(consent);
        lastSyncedConsentRef.current = consent;
      } catch (error) {
        console.error('CookieConsentProvider: Failed to update user consent:', error);
      }
    }
  };

  const acceptCookies = async () => {
    setCookieConsent('accepted');
    setConsentGiven('accepted');
    await updateUserConsent('accepted');

    // Note: Queue processing is handled by provider components when they detect consent change:
    // - MetaPixelProvider calls processMetaPixelQueue() to flush queued Meta Pixel events
    // - GoogleAnalyticsProvider calls processGAQueue() to flush queued GA events
    // Both providers monitor consentGiven via useEffect and process their respective queues
    // when consent transitions to 'accepted'
  };

  const declineCookies = async () => {
    setCookieConsent('rejected');
    setConsentGiven('rejected');
    await updateUserConsent('rejected');

    // Clear tracking cookies and queued events when consent is declined:
    // - handleGoogleAnalyticsConsent('rejected') clears GA cookies and queued GA events
    // - handleMetaPixelConsent('rejected') clears Meta cookies and triggers page reload
    // Queued events in sessionStorage are cleared to respect user's privacy choice
    handleGoogleAnalyticsConsent('rejected');
    handleMetaPixelConsent('rejected'); // This will reload the page
  };

  const hasConsent = consentGiven === 'accepted';
  const isUndecided = consentGiven === 'undecided';

  const value: CookieConsentContextType = {
    consentGiven,
    isMounted,
    hasConsent,
    isUndecided,
    acceptCookies,
    declineCookies,
  };

  return <CookieConsentContext.Provider value={value}>{children}</CookieConsentContext.Provider>;
};
